import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  Sparkles, 
  Lightbulb, 
  FileText, 
  Loader2, 
  ArrowRight,
  CheckCircle2,
  Zap,
  Target,
  TrendingUp,
  ArrowLeft
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ToolSEOContent from "@/components/ToolSEOContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AIBlogGenerator() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("generate");
  
  // Generate Blog State
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [wordCount, setWordCount] = useState(1500);
  const [generating, setGenerating] = useState(false);
  const [generatedBlog, setGeneratedBlog] = useState(null);
  
  // Ideas State
  const [niche, setNiche] = useState("");
  const [ideaCount, setIdeaCount] = useState(10);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [ideas, setIdeas] = useState([]);

  const handleGenerateBlog = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }
    
    setGenerating(true);
    setGeneratedBlog(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/blogs/ai/generate`, {
        topic: topic.trim(),
        keywords: keywords.split(",").map(k => k.trim()).filter(k => k),
        word_count: wordCount
      });
      
      if (response.data.success) {
        setGeneratedBlog(response.data.blog);
        toast.success("Blog generated successfully!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate blog");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateIdeas = async () => {
    if (!niche.trim()) {
      toast.error("Please enter a niche");
      return;
    }
    
    setLoadingIdeas(true);
    setIdeas([]);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/blogs/ai/ideas`, {
        niche: niche.trim(),
        count: ideaCount
      });
      
      if (response.data.success) {
        setIdeas(response.data.ideas || []);
        toast.success(`${response.data.ideas?.length || 0} ideas generated!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate ideas");
    } finally {
      setLoadingIdeas(false);
    }
  };

  const selectIdea = (idea) => {
    setTopic(idea.title);
    setKeywords(idea.keywords?.join(", ") || "");
    setActiveTab("generate");
    // Toast will be shown after state update
    setTimeout(() => {
      toast.success("Topic loaded! Click Generate to create the blog.");
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white py-12 pt-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/20 mr-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="p-3 bg-white/20 rounded-xl">
              <Sparkles className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">AI Blog Generator</h1>
              <p className="text-violet-200">Generate SEO-optimized blog articles in seconds</p>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            <Button
              variant={activeTab === "generate" ? "secondary" : "ghost"}
              onClick={() => setActiveTab("generate")}
              className={activeTab === "generate" ? "bg-white text-violet-700" : "text-white hover:bg-white/20"}
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Blog
            </Button>
            <Button
              variant={activeTab === "ideas" ? "secondary" : "ghost"}
              onClick={() => setActiveTab("ideas")}
              className={activeTab === "ideas" ? "bg-white text-violet-700" : "text-white hover:bg-white/20"}
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Get Ideas
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === "generate" ? (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-violet-600" />
                    Blog Details
                  </CardTitle>
                  <CardDescription>Enter your topic and let AI do the magic</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Topic / Title Idea</label>
                    <Input
                      placeholder="e.g., 10 Best SEO Tools for Small Businesses"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      data-testid="ai-blog-topic-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Target Keywords (comma separated)</label>
                    <Input
                      placeholder="e.g., seo tools, small business, free seo"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      data-testid="ai-blog-keywords-input"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Word Count: {wordCount}</label>
                    <input
                      type="range"
                      min="500"
                      max="3000"
                      step="100"
                      value={wordCount}
                      onChange={(e) => setWordCount(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>500</span>
                      <span>3000</span>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleGenerateBlog}
                    disabled={generating || !topic.trim()}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                    data-testid="ai-blog-generate-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating... (30-60 sec)
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Blog
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
              
              {/* Tips */}
              <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                <CardContent className="pt-6">
                  <h4 className="font-semibold text-violet-900 mb-3">Pro Tips for Better Results:</h4>
                  <ul className="space-y-2 text-sm text-violet-800">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600" />
                      Be specific with your topic for better targeted content
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600" />
                      Include 3-5 target keywords for optimal SEO
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600" />
                      1500-2000 words work best for SEO ranking
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            {/* Preview Section */}
            <div>
              {generatedBlog ? (
                <Card className="sticky top-4">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-green-100 text-green-700">Generated</Badge>
                      <Badge variant="outline">{generatedBlog.reading_time}</Badge>
                    </div>
                    <CardTitle className="text-xl mt-2">{generatedBlog.title}</CardTitle>
                    <CardDescription>{generatedBlog.meta_description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {generatedBlog.tags?.map((tag, i) => (
                        <Badge key={i} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                    
                    <div className="prose prose-sm max-h-64 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                      <div dangerouslySetInnerHTML={{ __html: generatedBlog.content?.substring(0, 1000) + "..." }} />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => navigate("/blog")} 
                        className="flex-1"
                      >
                        View in Blog Manager
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full flex items-center justify-center min-h-[400px] bg-gray-50/50">
                  <div className="text-center p-8">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Your generated blog will appear here</p>
                    <p className="text-sm text-gray-400 mt-2">Enter a topic and click Generate</p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        ) : (
          /* Ideas Tab */
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  Generate Blog Ideas
                </CardTitle>
                <CardDescription>Get AI-powered blog topic suggestions for your niche</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Input
                    placeholder="Enter your niche (e.g., digital marketing, fitness, cooking)"
                    value={niche}
                    onChange={(e) => setNiche(e.target.value)}
                    className="flex-1"
                    data-testid="ai-ideas-niche-input"
                  />
                  <select
                    value={ideaCount}
                    onChange={(e) => setIdeaCount(parseInt(e.target.value))}
                    className="border rounded-md px-3"
                  >
                    <option value={5}>5 ideas</option>
                    <option value={10}>10 ideas</option>
                    <option value={15}>15 ideas</option>
                  </select>
                  <Button
                    onClick={handleGenerateIdeas}
                    disabled={loadingIdeas || !niche.trim()}
                    data-testid="ai-ideas-generate-btn"
                  >
                    {loadingIdeas ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Lightbulb className="w-4 h-4 mr-2" />
                        Get Ideas
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Ideas Grid */}
            {ideas.length > 0 && (
              <div className="grid md:grid-cols-2 gap-4">
                {ideas.map((idea, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer group">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-3">
                        <Badge 
                          variant="outline" 
                          className={
                            idea.potential_traffic === "high" ? "text-green-600 border-green-300" :
                            idea.potential_traffic === "medium" ? "text-yellow-600 border-yellow-300" :
                            "text-gray-600 border-gray-300"
                          }
                        >
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {idea.potential_traffic} traffic
                        </Badge>
                        <Badge variant="secondary">{idea.difficulty}</Badge>
                      </div>
                      
                      <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-violet-700 transition-colors">
                        {idea.title}
                      </h3>
                      
                      <div className="flex flex-wrap gap-1 mb-4">
                        {idea.keywords?.slice(0, 3).map((kw, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {kw}
                          </Badge>
                        ))}
                      </div>
                      
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full group-hover:bg-violet-50 group-hover:text-violet-700 group-hover:border-violet-300"
                        onClick={() => selectIdea(idea)}
                      >
                        <Target className="w-4 h-4 mr-2" />
                        Use This Topic
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      <ToolSEOContent toolId="ai-blog" />
      <Footer />
    </div>
  );
}
